import { useEffect, useState, useCallback } from 'react';
import './pdfViewer.scss';
import { useIntersectionObserver } from '@wojtekmaj/react-hooks';
import { useRef } from 'react';
import { UilFileDownload, UilPrint, UilAngleUp, UilAngleDown, UilPlus, UilMinus } from '@iconscout/react-unicons';

import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const observerConfig = {
  threshold: 0,
};

function PageWithObserver({ pageNumber, setPageVisibility, ...otherProps }) {
  const [page, setPage] = useState();

  const onIntersectionChange = useCallback(
    ([entry]) => {
      if (pageNumber == 2 && !entry.isIntersecting) setPageVisibility(1);
      if (entry.isIntersecting) setPageVisibility(pageNumber);
    },
    [setPageVisibility],
  );

  useIntersectionObserver(page, observerConfig, onIntersectionChange);

  return (
    <div id={'p-' + pageNumber}>
      <Page canvasRef={setPage} pageNumber={pageNumber} {...otherProps} />
    </div>
  );
}

const PdfViewer = ({ file, onClose }) => {
  const zooms = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
  const zoomsMax = zooms.length - 1;

  const pageInputRef = useRef();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(3);
  const [pos, setPos] = useState({ x: 0, y: 0, scale: 1 });
  const [blobURL, setBlobURL] = useState(3);

  const setPageVisibility = useCallback((pageNumber) => {
    pageInputRef.current.value = pageNumber;
    setPageNumber(pageNumber);
  }, []);

  const onDocumentLoadSuccess = async (pdf) => {
    setNumPages(pdf.numPages);

    const data = await pdf.getData();
    const blob = new Blob([data], { type: 'application/pdf' });
    setBlobURL(URL.createObjectURL(blob));
  };

  const closeDoc = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onClose();
  };

  const onPageChange = (page) => {
    if (page && page <= numPages) {
      document.getElementById('p-' + page).scrollIntoView();
      setPageNumber(page);
    }
  };

  const zoomIn = () => {
    setScale((prev) => {
      const newValue = prev + 1;
      return newValue < zoomsMax ? newValue : zoomsMax;
    });
  };

  const zoomOut = () => {
    setScale((prev) => {
      const newValue = prev - 1;
      return newValue > 0 ? newValue : 0;
    });
  };

  const onScaleChange = (e) => {
    setScale(e.target.value);
  };

  const printHandler = async (e) => {
    const iframe = document.createElement('iframe');
    document.body.appendChild(iframe);

    iframe.style.display = 'none';
    iframe.src = blobURL;
    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow.print();
      }, 1);
    };
  };

  const downloadFromLink = (url, filename = 'file') => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename; // nazwa pliku do pobrania
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="pdf-viewer">
      <div className="pdf-viewer__header">
        <div className="flex pdf-viewer__header__left">
          <button className="flex items-center ml-2 mr-2" onClick={() => downloadFromLink(file, 'raport.pdf')}>
            <UilFileDownload
              size={28}
              style={{
                verticalAlign: 'initial',
              }}
            />
          </button>

          <button className="pdf-viewer__header__btn" onClick={printHandler}>
            <UilPrint
              size={25}
              style={{
                verticalAlign: 'initial',
              }}
            />
          </button>

          <button
            className="pdf-viewer__header__btn"
            onClick={(e) => {
              e.stopPropagation();
              onPageChange(pageNumber - 1);
            }}
          >
            <UilAngleUp
              size={25}
              style={{
                verticalAlign: 'initial',
              }}
            />
          </button>
          <button
            className="pdf-viewer__header__btn"
            onClick={(e) => {
              e.stopPropagation();
              onPageChange(pageNumber + 1);
            }}
          >
            <UilAngleDown
              size={25}
              style={{
                verticalAlign: 'initial',
              }}
            />
          </button>

          <div className="flex items-center form pagination-container">
            <input
              className="input-pagination"
              style={{ color: '#FAFAFA' }}
              ref={pageInputRef}
              onKeyDown={(e) => {
                e.stopPropagation();

                if (e.key === 'Enter') {
                  onPageChange(parseInt(e.target.value));
                }
              }}
              defaultValue={1}
              name="currentPage"
            ></input>
            <span className="span-separator">{' z '}</span>
            <span className="span-total">{numPages}</span>
          </div>
        </div>
        <div className="flex pdf-viewer__header__center">
          <button
            className="pdf-viewer__header__btn"
            onClick={(e) => {
              e.stopPropagation();
              zoomIn();
            }}
          >
            <UilPlus
              size={25}
              style={{
                verticalAlign: 'initial',
              }}
            />
          </button>
          <button
            className="pdf-viewer__header__btn"
            onClick={(e) => {
              e.stopPropagation();
              zoomOut();
            }}
          >
            <UilMinus
              size={25}
              style={{
                verticalAlign: 'initial',
              }}
            />
          </button>

          <select
            className="ml-2"
            style={{
              color: 'black',
            }}
            name="scale"
            value={scale}
            onChange={onScaleChange}
          >
            {zooms.map((v, i) => (
              <option value={i}>{v * 100}%</option>
            ))}
          </select>
        </div>
        <div className="flex pdf-viewer__header__right">
          <button className="pdf-viewer__close-btn" type="button" aria-label="Close" onClick={closeDoc}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>

      <div className="pdf-viewer__doc">
        <Document file={file} onLoadSuccess={onDocumentLoadSuccess} onLoadError={(err) => console.log(err)}>
          {Array.from(new Array(numPages), (el, index) => (
            <PageWithObserver
              className="mb-2"
              style={{
                backgroundColor: 'transparent',
              }}
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              scale={zooms[scale]}
              setPageVisibility={setPageVisibility}
            />
          ))}
        </Document>
      </div>
    </div>
  );
};

export default PdfViewer;
